import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Program, Account, IAccount, ProgramWritesAccount } from '@Core/models/codegen/area.models';
import { UserContext } from '@Core/contexts/user-context';
import { TenantContext } from '@Core/contexts/tenant-context';
import { ContractDate } from '@Core/utils/contract-date';

import { UserAccountsService } from '@Services/user-accounts.service';
import { LoadingService } from '@Services/loading.service';

import { AccountCreationModalComponent, AccountCreationModalResult } from '@Work/Shared/account-creation-modal/account-creation-modal.component';
import { BaseService } from '@Services/base.service';
import { Region } from '@Core/models/codegen/configuration.models';
import { ModelUtils } from '@Core/utils/model-utils';
import { FeatureFacetService } from '@Services/feature-facet.service';
import { TenantPermissions } from '@Core/enums/codegen/tenant-permissions.enum';
import { AuthService } from '@Services/auth.service';
import { LoggingService } from '@Core/services/logging.service';

@Component({
    standalone: false,
    selector: 'new-business-button',
    templateUrl: './new-business-button.component.html',
    styleUrls: ['./new-business-button.component.scss']
})

export class NewBusinessButtonComponent implements OnInit, OnDestroy {
    public loading: boolean = false;
    public programList: AvailableProgramsViewModel[] = [];
    public regions: Region[] = [];
    public programRequired: boolean;

    public clearanceRequired: boolean = false;
    public clearanceChatOpen: boolean = false;

    private ngUnsubscribe: Subject<any> = new Subject();

    constructor(
        private tenantContext: TenantContext,
        private userContext: UserContext,
        private service: UserAccountsService,
        private router: Router,
        private modalService: NgbModal,
        private loadingService: LoadingService,
        private facetService: FeatureFacetService,
        private authService: AuthService,
        private logger: LoggingService
    ) { }

    ngOnInit(): void {
        this.loading = true;
        this.userContext.getStore<Program>(new Program()).values
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(data => {
                this.programList = [];
                data.forEach(program => {
                    this.programList.push(
                        {
                            Id: program.Id,
                            Name: program.Name,
                        });
                });
                this.loading = false;
            });

        this.tenantContext.getStore<Region>(new Region()).values
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(data => {
                this.regions = data;
            });

        this.facetService.getAllFacets().subscribe(allFacets => {

            if (FeatureFacetService.checkIfFacetExists(TenantPermissions.Flag_Base_Work_Accounts_ExplicitProgramAccess,
                allFacets, 'work', this.authService.getAreaKey())) {
                this.programRequired = true;
            }

            if (FeatureFacetService.checkIfFacetExists(TenantPermissions.Flag_Base_Work_Accounts_AccountClearance,
                allFacets, 'work', this.authService.getAreaKey())) {
                this.clearanceRequired = true;
            }
        });
    }

    tryCreateNewAccount() {
        if (this.clearanceRequired) {
            this.clearance();
        } else {
            this.createNewAccount();
        }
    }

    createNewAccount(): void {
        if (this.loading) {
            return;
        }
        const modalRef = this.modalService.open(AccountCreationModalComponent, {
            ariaLabelledBy: 'modal-basic-title',
            backdrop: "static",
            size: 'lg'
        });
        const modalComponent = modalRef.componentInstance as AccountCreationModalComponent;
        const self = this;

        modalComponent.programs = this.programList;
        modalComponent.regions = this.regions;
        modalComponent.programRequired = this.programRequired

        modalRef.componentInstance.onCreateAccount.subscribe((result: AccountCreationModalResult) => {
            if (!result) {
                return;
            }
            self.loading = true;

            const account = new Account();
            account.Name = result.accountName;
            account.Code = result.accountCode;
            account.Dba = result.dba;
            account.FEIN = result.fein;
            account.LegalEntity = result.legalEntity;
            account.OtherLegalEntity = result.otherLegalEntity;
            account.DescriptionOfOperations = result.descriptionOfOperations;
            account.Phone = result.phone;
            account.Country = result.country;
            account.Address1 = result.address1;
            account.Address2 = result.address2;
            account.Address3 = result.address3;
            account.City = result.city;
            account.State = result.state;
            account.Zip = result.zip;
            account.County = result.county;
            account.Unincorporated = result.unincorporated;
            account.AccountTaxLocation = result.taxLocation;

            if (result.programId != null) {
                const programEdge = new ProgramWritesAccount();
                const program = this.userContext.get(ModelUtils.createDomainId(new Program(), result.programId)) as Program;
                programEdge.setIsWrittenFor(program);
                account.setIsWrittenFor([programEdge]);
            }

            const createAccountDTO: CreateAccountDTO = {
                Account: account.serialize(),
                ProducerId: result.producerId,
            };

            this.loadingService.loading.next(true);
            this.logger.logEvent(`Create Account via New Account button`);
            this.service.createAccount(createAccountDTO).subscribe({
                next: (response: any) => {
                    modalRef.close();
                    self.loadingService.loading.next(false);
                    self.loading = false;

                    const responseData = response.Content;
                    const accountId = responseData["Id"];

                    if (!responseData["Runs"]) {
                        self.router.navigate(['Work', 'Account', accountId], { queryParams: { startworkflow: result.startWorkflow ? true : null } });
                    } else {
                        const workflowId = responseData["Runs"][0]["@Relationship"].Id;
                        self.router.navigate(['Work', 'Account', accountId, 'Workflow', workflowId]);
                    }
                },
                error: (error) => {
                    modalRef.componentInstance.errorMessage = BaseService.convertCommonErrors(error?.error?.Errors[0]);

                    //Error occurred - set loading to false
                    this.loadingService.loading.next(false);
                    this.loading = false;
                }
            });
        }, (reason) => {
        });
    }

    clearance() {
        if (this.loading) {
            return;
        }
        this.clearanceChatOpen = true;
    }

    onClearanceCreateAccount() {
        this.clearanceChatOpen = false;
        this.createNewAccount();
    }

    onClearanceAccountSelected(accountId: string) {
        this.clearanceChatOpen = false;
        this.router.navigate(['Work', 'Account', accountId, 'Overview']);
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
};

export interface AvailableProgramsViewModel {
    Id: string;
    Name: string;
}

export interface AvailableProducersViewModel {
    Id: string;
    Name: string;
    Address1: string;
    Address2: string;
    Address3: string;
    City: string;
    State: string;
    Zip: string;
    Code: string;
    NameCodeCombo: string;
}

export interface CreateAccountDTO {
    Account: IAccount;
    ProducerId?: string;
    AgentId?: string;
    Program?: ProgramDTO;
    Workflow?: WorkflowDTO;
}

interface WorkflowDTO {
    ProgramId: string;
    WorkflowCreatedFromId: string;
    ProposedEffectiveDate: ContractDate;
    Notes: string;
}
interface ProgramDTO {
    ProgramId: string;
    ProposedEffectiveDate: ContractDate;
}